<script setup>
import { useAlert } from 'dashboard/composables';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useEventListener } from '@vueuse/core';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import LanguageDropdown from 'dashboard/components-next/LanguageDropdown/LanguageDropdown.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { vOnClickOutside } from '@vueuse/components';
import AddTemplate from './AddTemplate.vue';

const { t } = useI18n();
const store = useStore();
const router = useRouter();

const showAddPopup = ref(false);
const isSyncing = ref(false);

// Filter states
const selectedStatus = ref('');
const selectedLanguage = ref('');
const selectedChannelType = ref('');
const selectedInboxId = ref('');
const searchQuery = ref('');
const sortBy = ref('created_at');
const sortOrder = ref('desc');

// Dropdown states
const isStatusDropdownOpen = ref(false);
const isChannelDropdownOpen = ref(false);
const isInboxDropdownOpen = ref(false);

// Store-based computed properties
const templates = computed(
  () => store.getters['messageTemplates/getTemplates']
);
const uiFlags = computed(() => store.getters['messageTemplates/getUIFlags']);
const isLoading = computed(() => uiFlags.value.isFetching);
const isDeleting = computed(() => uiFlags.value.isDeleting);

// Filter options for dropdowns
const statusMenuItems = computed(() => [
  {
    action: 'statusSelect',
    value: '',
    label: t('SETTINGS.TEMPLATES.FILTERS.ALL_STATUSES'),
    isSelected: selectedStatus.value === '',
  },
  {
    action: 'statusSelect',
    value: 'approved',
    label: t('SETTINGS.TEMPLATES.FILTERS.APPROVED'),
    isSelected: selectedStatus.value === 'approved',
  },
  {
    action: 'statusSelect',
    value: 'pending',
    label: t('SETTINGS.TEMPLATES.FILTERS.PENDING'),
    isSelected: selectedStatus.value === 'pending',
  },
  {
    action: 'statusSelect',
    value: 'rejected',
    label: t('SETTINGS.TEMPLATES.FILTERS.REJECTED'),
    isSelected: selectedStatus.value === 'rejected',
  },
]);

const inboxes = computed(() => store.getters['inboxes/getWhatsAppInboxes']);

const inboxMenuItems = computed(() => [
  {
    action: 'inboxSelect',
    value: '',
    label: t('SETTINGS.TEMPLATES.FILTERS.ALL_INBOXES') || 'All Inboxes',
    isSelected: selectedInboxId.value === '',
  },
  ...inboxes.value.map(inbox => ({
    action: 'inboxSelect',
    value: inbox.id,
    label: inbox.name,
    isSelected: selectedInboxId.value === inbox.id,
  })),
]);

const channelMenuItems = computed(() => [
  {
    action: 'channelSelect',
    value: '',
    label: t('SETTINGS.TEMPLATES.FILTERS.ALL_CHANNELS'),
    isSelected: selectedChannelType.value === '',
  },
  {
    action: 'channelSelect',
    value: 'Channel::Whatsapp',
    label: 'WhatsApp',
    isSelected: selectedChannelType.value === 'Channel::Whatsapp',
  },
]);

const selectedStatusLabel = computed(() => {
  const selected = statusMenuItems.value.find(
    item => item.value === selectedStatus.value
  );
  return selected
    ? selected.label
    : t('SETTINGS.TEMPLATES.FILTERS.ALL_STATUSES');
});

const selectedInboxLabel = computed(() => {
  const selected = inboxMenuItems.value.find(
    item => item.value === selectedInboxId.value
  );
  return selected
    ? selected.label
    : t('SETTINGS.TEMPLATES.FILTERS.ALL_INBOXES') || 'All Inboxes';
});

const selectedChannelLabel = computed(() => {
  const selected = channelMenuItems.value.find(
    item => item.value === selectedChannelType.value
  );
  return selected
    ? selected.label
    : t('SETTINGS.TEMPLATES.FILTERS.ALL_CHANNELS');
});

const filteredTemplates = computed(() => {
  const filtered = templates.value.filter(template => {
    const matchesStatus =
      !selectedStatus.value || template.status === selectedStatus.value;
    const matchesLanguage =
      !selectedLanguage.value || template.language === selectedLanguage.value;
    const matchesChannel =
      !selectedChannelType.value ||
      template.channel_type === selectedChannelType.value;
    const matchesInbox =
      !selectedInboxId.value || template.inbox_id === selectedInboxId.value;
    const matchesSearch =
      !searchQuery.value ||
      template.name.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
      template.category.toLowerCase().includes(searchQuery.value.toLowerCase());

    return (
      matchesStatus &&
      matchesLanguage &&
      matchesChannel &&
      matchesInbox &&
      matchesSearch
    );
  });

  // Sort templates
  return filtered.sort((a, b) => {
    const aValue = a[sortBy.value] || '';
    const bValue = b[sortBy.value] || '';

    if (sortOrder.value === 'asc') {
      return aValue > bValue ? 1 : -1;
    }
    return aValue < bValue ? 1 : -1;
  });
});

const fetchTemplates = async () => {
  try {
    await store.dispatch('messageTemplates/get');
  } catch (error) {
    useAlert(t('SETTINGS.TEMPLATES.API.FETCH_ERROR'));
  }
};

const openAddPopup = () => {
  showAddPopup.value = true;
};

const hideAddPopup = () => {
  showAddPopup.value = false;
};

const syncTemplates = async () => {
  isSyncing.value = true;
  try {
    // Get IDs from currently active WhatsApp inboxes to avoid syncing deleted ones
    const inboxIds = inboxes.value.map(inbox => inbox.id);

    if (inboxIds.length === 0) {
      useAlert(t('SETTINGS.TEMPLATES.SYNC.NO_INBOXES'));
      return;
    }

    // Sync all inboxes in parallel
    await Promise.all(
      inboxIds.map(inboxId => store.dispatch('inboxes/syncTemplates', inboxId))
    );

    // Refresh the templates list
    await fetchTemplates();

    useAlert(t('SETTINGS.TEMPLATES.SYNC.SUCCESS'));
  } catch (error) {
    useAlert(t('SETTINGS.TEMPLATES.SYNC.ERROR'));
  } finally {
    isSyncing.value = false;
  }
};

const handleStatusAction = ({ value }) => {
  selectedStatus.value = value;
  isStatusDropdownOpen.value = false;
};

const handleChannelAction = ({ value }) => {
  selectedChannelType.value = value;
  isChannelDropdownOpen.value = false;
};

const handleInboxAction = ({ value }) => {
  selectedInboxId.value = value;
  isInboxDropdownOpen.value = false;
};

const toggleStatusDropdown = () => {
  isStatusDropdownOpen.value = !isStatusDropdownOpen.value;
};

const toggleChannelDropdown = () => {
  isChannelDropdownOpen.value = !isChannelDropdownOpen.value;
};

const toggleInboxDropdown = () => {
  isInboxDropdownOpen.value = !isInboxDropdownOpen.value;
};

const closeStatusDropdown = () => {
  isStatusDropdownOpen.value = false;
};

const closeChannelDropdown = () => {
  isChannelDropdownOpen.value = false;
};

const closeInboxDropdown = () => {
  isInboxDropdownOpen.value = false;
};

const getStatusColor = status => {
  switch (status) {
    case 'approved':
      return 'text-green-600';
    case 'pending':
      return 'text-yellow-600';
    case 'rejected':
      return 'text-red-600';
    default:
      return 'text-gray-600';
  }
};

const formatCategory = category => {
  return category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

const getTemplateIcon = category => {
  switch (category) {
    case 'marketing':
      return 'i-lucide-megaphone';
    case 'authentication':
      return 'i-lucide-shield-check';
    case 'utility':
      return 'i-lucide-settings';
    default:
      return 'i-lucide-message-square';
  }
};

const formatDate = dateValue => {
  if (!dateValue) return 'N/A';
  // Handle Unix timestamp (number) or date string
  const date =
    typeof dateValue === 'number'
      ? new Date(dateValue * 1000)
      : new Date(dateValue);
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(date);
};

const toggleSort = () => {
  sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc';
};

const onKeydown = e => {
  if (showAddPopup.value && e.code === 'Escape') {
    hideAddPopup();
    e.stopPropagation();
  }
};

useEventListener(document, 'keydown', onKeydown);

// Delete confirmation modal
const showDeletePopup = ref(false);
const selectedTemplate = ref({});

const openDelete = template => {
  showDeletePopup.value = true;
  selectedTemplate.value = template;
};

const closeDelete = () => {
  showDeletePopup.value = false;
  selectedTemplate.value = {};
};

const confirmDeletion = async () => {
  try {
    await store.dispatch('messageTemplates/delete', selectedTemplate.value.id);
    useAlert(t('SETTINGS.TEMPLATES.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('SETTINGS.TEMPLATES.DELETE.API.ERROR_MESSAGE'));
  } finally {
    closeDelete();
  }
};

const confirmDeleteTitle = computed(() =>
  t('SETTINGS.TEMPLATES.DELETE.CONFIRM.TITLE', {
    templateName: selectedTemplate.value.name,
  })
);

const deleteConfirmText = computed(
  () =>
    `${t('SETTINGS.TEMPLATES.DELETE.CONFIRM.YES')} ${selectedTemplate.value.name}`
);

const deleteRejectText = computed(() =>
  t('SETTINGS.TEMPLATES.DELETE.CONFIRM.NO')
);

const confirmPlaceHolderText = computed(() =>
  t('SETTINGS.TEMPLATES.DELETE.CONFIRM.PLACE_HOLDER', {
    templateName: selectedTemplate.value.name,
  })
);

const goToWhatsAppCampaigns = () => {
  const accountId = router.currentRoute.value.params.accountId;
  router.push({
    name: 'campaigns_whatsapp_index',
    params: { accountId },
  });
};

onMounted(() => {
  fetchTemplates();
});
</script>

<template>
  <div class="flex flex-col w-full h-full font-inter">
    <!-- Header -->
    <div class="flex items-center justify-between mb-6">
      <div class="flex items-center gap-4">
        <h1 class="text-2xl font-semibold text-n-slate-12">
          {{ t('SETTINGS.TEMPLATES.HEADER.TITLE') }}
        </h1>
      </div>
      <div class="flex items-center gap-3">
        <!-- Search Input -->
        <div class="hidden sm:flex items-center gap-2 w-80">
          <Input
            v-model="searchQuery"
            type="search"
            :placeholder="t('SETTINGS.TEMPLATES.SEARCH_PLACEHOLDER')"
            :custom-input-class="[
              'h-8 [&:not(.focus)]:!border-transparent bg-n-alpha-2 dark:bg-n-solid-1 ltr:!pl-8 rtl:!pr-8 !py-1',
            ]"
            class="w-full"
          >
            <template #prefix>
              <Icon
                icon="i-lucide-search"
                class="absolute -translate-y-1/2 text-n-slate-11 size-4 top-1/2 ltr:left-2 rtl:right-2"
              />
            </template>
          </Input>
        </div>

        <Button
          label="WhatsApp Campaigns"
          icon="i-lucide-megaphone"
          variant="outline"
          color="slate"
          size="sm"
          class="flex-shrink-0"
          @click="goToWhatsAppCampaigns"
        />

        <Button
          icon="i-lucide-refresh-cw"
          slate
          :label="t('SETTINGS.TEMPLATES.ACTIONS.SYNC_TEMPLATES')"
          :is-loading="isSyncing"
          class="flex-shrink-0"
          @click="syncTemplates"
        />

        <div class="relative flex-shrink-0">
          <Button
            icon="i-lucide-plus"
            blue
            :label="t('SETTINGS.TEMPLATES.ACTIONS.NEW_TEMPLATE')"
            class="hidden sm:flex"
            @click="openAddPopup"
          />
          <!-- Icon-only button for small screens -->
          <Button
            icon="i-lucide-plus"
            blue
            class="sm:hidden"
            @click="openAddPopup"
          />
          <!-- Add Template Popover -->
          <div v-if="showAddPopup" class="absolute right-0 top-full mt-2 z-50">
            <div
              class="bg-n-alpha-3 border border-n-strong rounded-xl shadow-sm backdrop-blur-[100px] w-96 max-h-[80vh] overflow-auto"
              @click.stop
            >
              <AddTemplate @close="hideAddPopup" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Filters -->
    <div class="flex items-center gap-3 mb-6">
      <!-- Inbox Filter -->
      <div v-on-click-outside="closeInboxDropdown" class="relative w-48">
        <button
          type="button"
          class="flex items-center justify-between gap-2 h-10 px-3 text-sm rounded-lg text-n-slate-12 transition-colors focus:outline-none focus:ring-2 focus:ring-n-blue-5 w-full border border-n-weak bg-n-solid-1 hover:bg-n-solid-2"
          @click="toggleInboxDropdown"
        >
          <span class="truncate">{{ selectedInboxLabel }}</span>
          <Icon
            icon="i-lucide-chevron-down"
            class="size-4 flex-shrink-0 transition-transform"
            :class="{ 'rotate-180': isInboxDropdownOpen }"
          />
        </button>
        <DropdownMenu
          v-if="isInboxDropdownOpen"
          :menu-items="inboxMenuItems"
          class="absolute top-full mt-1 left-0 right-0 w-full z-50"
          @action="handleInboxAction"
        />
      </div>

      <!-- Status Filter -->
      <div v-on-click-outside="closeStatusDropdown" class="relative w-48">
        <button
          type="button"
          class="flex items-center justify-between gap-2 h-10 px-3 text-sm rounded-lg text-n-slate-12 transition-colors focus:outline-none focus:ring-2 focus:ring-n-blue-5 w-full border border-n-weak bg-n-solid-1 hover:bg-n-solid-2"
          @click="toggleStatusDropdown"
        >
          <span class="truncate">{{ selectedStatusLabel }}</span>
          <Icon
            icon="i-lucide-chevron-down"
            class="size-4 flex-shrink-0 transition-transform"
            :class="{ 'rotate-180': isStatusDropdownOpen }"
          />
        </button>
        <DropdownMenu
          v-if="isStatusDropdownOpen"
          :menu-items="statusMenuItems"
          class="absolute top-full mt-1 left-0 right-0 w-full z-50"
          @action="handleStatusAction"
        />
      </div>

      <!-- Channel Filter -->
      <div v-on-click-outside="closeChannelDropdown" class="relative w-48">
        <button
          type="button"
          class="flex items-center justify-between gap-2 h-10 px-3 text-sm rounded-lg text-n-slate-12 transition-colors focus:outline-none focus:ring-2 focus:ring-n-blue-5 w-full border border-n-weak bg-n-solid-1 hover:bg-n-solid-2"
          @click="toggleChannelDropdown"
        >
          <span class="truncate">{{ selectedChannelLabel }}</span>
          <Icon
            icon="i-lucide-chevron-down"
            class="size-4 flex-shrink-0 transition-transform"
            :class="{ 'rotate-180': isChannelDropdownOpen }"
          />
        </button>
        <DropdownMenu
          v-if="isChannelDropdownOpen"
          :menu-items="channelMenuItems"
          class="absolute top-full mt-1 left-0 right-0 w-full z-50"
          @action="handleChannelAction"
        />
      </div>

      <!-- Language Filter -->
      <div class="relative w-48">
        <LanguageDropdown
          v-model="selectedLanguage"
          :placeholder="t('SETTINGS.TEMPLATES.FILTERS.ALL_LANGUAGES')"
          size="md"
          variant="outline"
          show-search
        />
      </div>
    </div>

    <!-- Templates List -->
    <div v-if="isLoading" class="flex items-center justify-center py-12">
      <woot-loading-state :message="$t('SETTINGS.TEMPLATES.LOADING')" />
    </div>

    <div
      v-else-if="!filteredTemplates.length"
      class="flex items-center justify-center py-12 text-n-slate-11"
    >
      {{ t('SETTINGS.TEMPLATES.LIST.404') }}
    </div>

    <div v-else class="flex-1 space-y-3">
      <!-- Sort Controls -->
      <div class="flex items-center justify-end gap-2 pb-2">
        <button
          type="button"
          class="flex items-center gap-2 px-3 py-2 text-sm text-n-slate-11 hover:text-n-slate-12 transition-colors"
          @click="toggleSort"
        >
          <span>{{ t('CAMPAIGN.SORT_BY_DATE_CREATED') }}</span>
          <span
            class="size-4 transition-transform"
            :class="
              sortOrder === 'asc' ? 'i-lucide-arrow-up' : 'i-lucide-arrow-down'
            "
          />
        </button>
      </div>
      <div
        v-for="template in filteredTemplates"
        :key="template.id"
        class="flex items-center justify-between p-4 border border-n-weak rounded-lg bg-n-solid-2 hover:bg-n-solid-3 transition-colors cursor-pointer"
        @click="
          $router.push({
            name: 'template_edit',
            params: { templateId: template.id },
          })
        "
      >
        <div class="flex items-center gap-4 flex-1">
          <!-- Template Icon -->
          <div
            class="flex items-center justify-center w-8 h-8 bg-n-solid-3 rounded"
          >
            <span
              :class="getTemplateIcon(template.category)"
              class="size-4 text-n-slate-11"
            />
          </div>

          <!-- Template Details -->
          <div class="flex-1">
            <div class="flex items-center gap-3 mb-1">
              <h3 class="text-sm font-medium text-n-slate-12">
                {{ template.name }}
              </h3>
              <span
                class="text-xs font-medium"
                :class="getStatusColor(template.status)"
              >
                {{
                  template.status.charAt(0).toUpperCase() +
                  template.status.slice(1)
                }}
              </span>
            </div>
            <div class="flex items-center gap-4 text-sm text-n-slate-11">
              <div class="flex items-center gap-1">
                <span
                  :class="getTemplateIcon(template.category)"
                  class="size-3"
                />
                <span>{{ formatCategory(template.category) }}</span>
              </div>
              <div class="flex items-center gap-1">
                <span class="i-lucide-globe size-3" />
                <span>{{ template.language.toUpperCase() }}</span>
              </div>
              <div class="flex items-center gap-1">
                <span class="i-lucide-calendar size-3" />
                <span>{{ formatDate(template.created_at) }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Actions -->
        <div class="flex items-center gap-1" @click.stop>
          <Button
            v-tooltip.top="$t('SETTINGS.TEMPLATES.ACTIONS.VIEW_EDIT')"
            icon="i-lucide-pencil"
            blue
            xs
            faded
            @click="
              $router.push({
                name: 'template_edit',
                params: { templateId: template.id },
              })
            "
          />
          <Button
            v-tooltip.top="$t('SETTINGS.TEMPLATES.ACTIONS.DELETE')"
            icon="i-lucide-trash-2"
            ruby
            xs
            faded
            @click="openDelete(template)"
          />
        </div>
      </div>
    </div>

    <!-- Backdrop for mobile/overlay when popover is open -->
    <div v-if="showAddPopup" class="fixed inset-0 z-40" @click="hideAddPopup" />

    <!-- Delete Confirmation Modal -->
    <woot-confirm-delete-modal
      v-if="showDeletePopup"
      v-model:show="showDeletePopup"
      :title="confirmDeleteTitle"
      :message="$t('SETTINGS.TEMPLATES.DELETE.CONFIRM.MESSAGE')"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
      :confirm-value="selectedTemplate.name"
      :confirm-place-holder-text="confirmPlaceHolderText"
      :is-loading="isDeleting"
      @on-confirm="confirmDeletion"
      @on-close="closeDelete"
    />
  </div>
</template>
